import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {ChartModule} from 'primeng/chart';
import {lastValueFrom} from 'rxjs';
import {DashboardService} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {Loader} from '../../../../components/loader/loader';
import {QueryKey} from '../../../../constants/query-key';
import {categoricalColor, fadedCategoricalColor, horizontalBarOptions, readCssVariable} from '../../charts/chart-theme';
import {formatSeconds, toHours} from '../../dashboard.utils';

type BreakdownTab = 'type' | 'customer';

@Component({
  selector: 'elcia-dashboard-time-breakdown',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChartModule, ElciaCard, Loader, TranslatePipe],
  templateUrl: './dashboard-time-breakdown.html',
  host: {class: 'block min-w-0'},
})
export class DashboardTimeBreakdown {
  private readonly dashboardService = inject(DashboardService);
  private readonly translate = inject(TranslateService);

  protected readonly tab = signal<BreakdownTab>('customer');

  private readonly statsQuery = injectQuery(() => ({
    queryKey: [QueryKey.DASHBOARD_TIMESHEET_STATS],
    queryFn: () => lastValueFrom(this.dashboardService.getDashboardTimesheetStats()),
  }));

  protected readonly isLoading = computed(() => this.statsQuery.isPending());
  private readonly byType = computed(() => this.statsQuery.data()?.stats?.byType ?? []);
  private readonly byCustomer = computed(() => this.statsQuery.data()?.stats?.byCustomer ?? []);

  protected readonly hasData = computed(() =>
    this.tab() === 'type' ? this.byType().length > 0 : this.byCustomer().length > 0);

  protected readonly showLegend = computed(() => this.tab() === 'type');

  protected readonly data = computed(() =>
    this.tab() === 'type' ? this.typeData() : this.customerData());

  protected readonly options = computed(() =>
    horizontalBarOptions(hours => formatSeconds(hours * 3600), this.showLegend()));

  protected setTab(tab: BreakdownTab): void {
    this.tab.set(tab);
  }

  protected tabClass(tab: BreakdownTab): string {
    return this.tab() === tab
      ? 'border-primary-500 text-primary-600 font-semibold'
      : 'border-transparent text-[var(--p-surface-500)] hover:text-[var(--p-surface-700)]';
  }

  private typeData() {
    const rows = this.byType();

    // Timesheet types carry no colour in the database, so each one takes a hue from the categorical
    // palette. The colour then identifies the type, and the two months are told apart by opacity.
    return {
      labels: rows.map(row => row.name),
      datasets: [
        {
          label: this.translate.instant('dashboard.chart.previous-month'),
          data: rows.map(row => toHours(row.previousMonthSeconds)),
          backgroundColor: rows.map((_, index) => fadedCategoricalColor(index)),
          borderRadius: 4,
        },
        {
          label: this.translate.instant('dashboard.chart.current-month'),
          data: rows.map(row => toHours(row.currentMonthSeconds)),
          backgroundColor: rows.map((_, index) => categoricalColor(index)),
          borderRadius: 4,
        },
      ],
    };
  }

  private customerData() {
    const rows = this.byCustomer();
    const otherLabel = this.translate.instant('dashboard.breakdown.other-customers');

    return {
      // The back sends an empty name on the aggregated row: labels are interface text, it does not
      // produce any.
      labels: rows.map(row => (row.customerId == null ? otherLabel : row.name)),
      datasets: [
        {
          data: rows.map(row => toHours(row.seconds)),
          backgroundColor: rows.map(row =>
            row.customerId == null
              ? readCssVariable('--p-surface-300', '#cbd5e1')
              : readCssVariable('--p-primary-500', '#3b82f6')),
          borderRadius: 4,
        },
      ],
    };
  }
}
